import type { AdminPaymentRow, AdminWebhookRow } from '@trendator/shared';
import { useEffect, useState } from 'react';
import { Link } from '../components/Layout';
import { Card, Empty, ErrorBox, FilterButton, Input, Loading, Modal, Status, Table, type Tone } from '../components/ui';
import { cn } from '../lib/cn';
import { api } from '../lib/api';
import { dateTime, num, rub, shortId, userName, when } from '../lib/format';
import { useLoad } from '../lib/hooks';
import { setQuery } from '../lib/router';

const FILTERS = [
  ['all', 'Все'],
  ['paid', 'Оплачены'],
  ['open', 'Ждут оплаты'],
  ['stuck', 'Зависли'],
  ['failed', 'Отмена / возврат'],
] as const;
type Counts = Record<(typeof FILTERS)[number][0], number>;

const STATUS: Record<string, { label: string; tone: Tone }> = {
  created: { label: 'Создан', tone: 'idle' },
  pending: { label: 'Ждёт оплаты', tone: 'run' },
  paid: { label: 'Оплачен', tone: 'ok' },
  canceled: { label: 'Отменён', tone: 'idle' },
  expired: { label: 'Истёк', tone: 'idle' },
  refunded: { label: 'Возврат', tone: 'bad' },
  chargeback: { label: 'Чарджбэк', tone: 'bad' },
};
const PROVIDER: Record<string, string> = { rollypay: 'RollyPay', cryptobot: 'Crypto Pay' };
const METHOD: Record<string, string> = { card_sbp: 'карта / СБП', usdt: 'USDT' };

export function Payments({ query }: { query: URLSearchParams }) {
  const view = query.get('view') === 'webhooks' ? 'webhooks' : 'payments';
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3.5">
        <h1 className="m-0 text-2xl font-extrabold tracking-tight lg:text-[30px]">Платежи</h1>
        <div className="inline-flex rounded-xl border border-line bg-card p-1">
          {(['payments', 'webhooks'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setQuery({ view: v === 'payments' ? null : v, filter: null, q: null })}
              className={cn('h-8 cursor-pointer rounded-lg px-3.5 text-sm font-semibold', view === v ? 'bg-accent text-on-accent' : 'text-soft hover:text-white')}
            >
              {v === 'payments' ? 'Платежи' : 'Лог вебхуков'}
            </button>
          ))}
        </div>
      </div>
      {view === 'payments' ? <PaymentList query={query} /> : <Webhooks />}
    </>
  );
}

function PaymentList({ query }: { query: URLSearchParams }) {
  const filter = query.get('filter') ?? 'all';
  const q = query.get('q') ?? '';
  const [search, setSearch] = useState(q);
  const list = useLoad(() => api.get<{ items: AdminPaymentRow[]; counts: Counts }>(`/payments?filter=${filter}${q ? `&q=${encodeURIComponent(q)}` : ''}`), [filter, q]);
  useEffect(() => {
    const t = setTimeout(() => search !== q && setQuery({ q: search.trim() || null }), 400);
    return () => clearTimeout(t);
  }, [search, q]);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(([id, label]) => (
          <FilterButton key={id} on={filter === id} onClick={() => setQuery({ filter: id === 'all' ? null : id })}>
            {label} <span className="opacity-70">{list.data ? num(list.data.counts[id]) : ''}</span>
          </FilterButton>
        ))}
        <label className="ml-auto min-w-[220px] flex-[0_1_320px]">
          <span className="sr-only">Поиск</span>
          <Input placeholder="ID платежа, id провайдера, @юзер" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 bg-card text-sm" />
        </label>
      </div>
      <Card className="px-2 pt-2 pb-1">
        {list.error ? <ErrorBox text={list.error} onRetry={() => void list.reload()} /> : null}
        {!list.data && list.loading ? <Loading /> : null}
        {list.data && list.data.items.length === 0 ? <Empty>Платежей нет</Empty> : null}
        {list.data && list.data.items.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Юзер</th>
                <th>Провайдер</th>
                <th>Пакет</th>
                <th>Статус</th>
                <th className="text-right!">Сумма</th>
                <th className="text-right!">Бонус</th>
                <th className="text-right!">Время</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((p) => {
                const st = p.stuck ? { label: 'Завис', tone: 'warn' as Tone } : (STATUS[p.status] ?? { label: p.status, tone: 'idle' as Tone });
                return (
                  <tr key={p.id}>
                    <td className="font-mono text-xs text-muted" title={p.providerPaymentId ?? ''}>
                      #{shortId(p.id)}
                    </td>
                    <td>
                      <Link to={`/users/${p.user.id}`} className="font-semibold text-white">
                        {userName(p.user)}
                      </Link>
                    </td>
                    <td className="text-soft">
                      {PROVIDER[p.provider] ?? p.provider} · {METHOD[p.method] ?? p.method}
                      {p.test ? <span className="ml-1.5 rounded bg-warn/14 px-1.5 text-[11px] font-bold text-warn">ТЕСТ</span> : null}
                    </td>
                    <td className="text-muted">{p.packageId}</td>
                    <td>
                      <Status tone={st.tone}>{st.label}</Status>
                    </td>
                    <td className="text-right font-semibold">{rub(p.amount)}</td>
                    <td className="text-right text-muted">{p.bonus ? `+${rub(p.bonus)}` : '—'}</td>
                    <td className="text-right text-muted">{when(p.paidAt ?? p.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : null}
      </Card>
      <span className="text-xs text-dim">Завис — создан больше 30 минут назад и не закрыт: крон сверяет статус у провайдера, при оплате кредиты придут сами.</span>
    </>
  );
}

function Webhooks() {
  const list = useLoad(() => api.get<{ items: AdminWebhookRow[] }>('/webhooks'));
  const [open, setOpen] = useState<AdminWebhookRow | null>(null);
  return (
    <>
      <Card className="px-2 pt-2 pb-1">
        {list.error ? <ErrorBox text={list.error} onRetry={() => void list.reload()} /> : null}
        {!list.data && list.loading ? <Loading /> : null}
        {list.data && list.data.items.length === 0 ? <Empty>Вебхуков ещё не было</Empty> : null}
        {list.data && list.data.items.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <th>#</th>
                <th>Откуда</th>
                <th>Idempotency key</th>
                <th className="text-right!">Получен</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((w) => (
                <tr key={w.id} className="cursor-pointer hover:[&_td]:bg-white/3" onClick={() => setOpen(w)}>
                  <td className="text-muted">{w.id}</td>
                  <td className="font-semibold">{PROVIDER[w.provider] ?? w.provider}</td>
                  <td className="max-w-[420px] truncate font-mono text-xs text-text2">{w.idempotencyKey}</td>
                  <td className="text-right text-muted">{dateTime(w.receivedAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>
      <span className="text-xs text-dim">Каждый вебхук обрабатывается ровно один раз: повтор с тем же ключом в журнал не попадает.</span>
      <Modal open={!!open} onClose={() => setOpen(null)} title={`Вебхук #${open?.id ?? ''}`}>
        <pre className="max-h-[60vh] overflow-auto rounded-xl bg-field p-3 font-mono text-xs leading-relaxed text-text2">{JSON.stringify(open?.payload ?? null, null, 2)}</pre>
      </Modal>
    </>
  );
}
